import React from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import {
  ArrowRight,
  Plane,
  Hotel,
  MapPin,
  Utensils,
  Car,
  Users,
  Handshake,
  Ticket,
  Ship,
  UserCheck,
  Globe,
  Wine,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import MaskedReveal from "@/components/motion/MaskedReveal";
import HexMark from "@/components/motion/HexMark";
import ClientLogos from "@/components/sections/ClientLogos";

const IMG = {
  hero: "/vip-film/hero-band-stage.webp",
  taxi: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&w=1600&q=80",
  sail: "/vip-film/leisure-sailboat.webp",
  yacht: "/vip-film/vip-plane.webp",
  friends: "/vip-film/accommodations-villa.webp",
  wedding: "/vip-film/weddings-gown.webp",
  tunic: "https://images.unsplash.com/photo-1519741347686-c1e0aadf4611?auto=format&fit=crop&w=1600&q=80",
};

const CAPABILITIES = [
  { icon: Hotel, name: "Hotel & Site Accommodations", body: "Five-star hotels, private estates, villas, and VIP guest preferences." },
  { icon: Plane, name: "Travel Planning", body: "Private flights, route logic, transfers, and schedule design." },
  { icon: MapPin, name: "Site Selection & Management", body: "Venue readiness, local coordination, and on-site execution." },
  { icon: Utensils, name: "Food & Beverage Services", body: "Curating top chefs and luxury, fine-dining experiences." },
  { icon: Car, name: "Transportation Coordination", body: "Private black car, aircraft, and group planning." },
  { icon: Users, name: "Guest Management & Engagement", body: "Registration, ticket sales, and communication." },
  { icon: Handshake, name: "Supplier & DMC Management", body: "Trusted partners vetted, contracted, and coordinated." },
  { icon: ShieldCheck, name: "Hotel Negotiations & Contracts", body: "Terms shaped around your program, not the property's." },
  { icon: Ticket, name: "Air, Rail & Ride-Share Support", body: "Groups, individuals, and last-minute reroutes." },
  { icon: Sparkles, name: "Entertainment Coordination", body: "Talent, riders, backstage flow, and stage-side ops." },
  { icon: UserCheck, name: "On-Site Travel Director Staffing", body: "Certified travel directors and floor teams." },
  { icon: Globe, name: "Passport & Visa Engagement", body: "Documentation guidance for international travel." },
];

const WHY = [
  {
    icon: Wine,
    title: "High-touch support",
    body: "Deep experience across luxury travel, stays, and on-site operations — delivered with the discretion VIP clients expect.",
  },
  {
    icon: UserCheck,
    title: "Personalized for VIP clients",
    body: "Every itinerary is tailored — preferences, protocols, and program-specific requirements all built into the plan.",
  },
  {
    icon: ShieldCheck,
    title: "Perfection under pressure",
    body: "Last-minute changes, tight deadlines, and unexpected complexity — turned into a competitive advantage.",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "One of the world's most talented event planners and most effective motivational speakers. Sir Dion carries my highest and best recommendation.",
    name: "Professor Joe Goldblatt",
    role: "Emeritus, International Event Management",
  },
  {
    quote:
      "Dion was phenomenal through the planning and execution of our senior-leadership conference — consistent updates, meaningful input, and negotiation skills that saved money while optimizing the guest experience.",
    name: "Dr. Tiffani Braswell-Worthy, PMP",
    role: "Executive Coach · Diversity, Equity & Inclusion Strategist",
  },
  {
    quote:
      "Extremely personable and dedicated. He offers creative solutions and delivers white-glove service — an asset to any organization looking for a seasoned meeting professional.",
    name: "Joyce Wooden",
    role: "Sr. Manager, Global Meetings & Events",
  },
];

export default function VipFilm() {
  return (
    <div className="bg-white text-[var(--text)]">
      <Seo
        title="VIP & Film Destination Management — Tours, Executive Travel & Productions"
        description="Bespoke destination management for tours, film productions, and VIP travel. Ground logistics, private aviation coordination, hospitality, and on-site directors — handled with white-glove discretion, worldwide."
        path="/services/vip-film"
        image={IMG.hero}
        keywords="VIP travel management, film production logistics, destination management company, tour logistics, executive travel, private hospitality, DMC"
      />
      {/* HERO */}
      <section className="relative pt-28 md:pt-32 px-4 md:px-6">
        <div className="relative max-w-[1600px] mx-auto rounded-[28px] md:rounded-[36px] overflow-hidden min-h-[560px] md:min-h-[660px]">
          <img
            src={IMG.hero}
            alt="Luxury traveler in yellow"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050A14]/50 via-[#050A14]/40 to-[#050A14]/85" />
          <div className="absolute -top-32 -left-24 w-[500px] h-[500px] rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="absolute -bottom-32 -right-24 w-[600px] h-[600px] rounded-full bg-sky-500/20 blur-3xl" />

          <div className="relative px-4 md:px-14 pt-16 md:pt-24 pb-24 md:pb-28">
            <div className="flex items-center justify-center">
              <span className="chip !bg-white/10 !text-cyan-300 !border-cyan-300/25">
                <span className="chip-dot" /> VIP &amp; Film · Destination Management
              </span>
            </div>
            <h1 className="hero-headline text-white text-center mt-8 md:mt-10 leading-[0.95] text-[46px] sm:text-[60px] md:text-[76px] lg:text-[92px] max-w-5xl mx-auto text-balance px-2">
              VIP &amp; Film —{" "}
              <span className="hero-accent">logistics for the world stage.</span>
            </h1>
            <div className="flex justify-center mt-8">
              <span className="pulse-bar w-32 md:w-48" />
            </div>
            <p className="text-center text-white/85 text-base md:text-lg leading-relaxed max-w-3xl mx-auto mt-8">
              Tour &amp; VIP Luxury Travel · Accommodations · Concert Support ·
              Location Search &amp; Site Management · Luxury Travel · Tour Management.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Link to="/contact" className="btn-primary">
                Request a Quote <ArrowRight size={16} />
              </Link>
              <Link to="/services" className="btn-dark backdrop-blur">
                All Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY M.E. TEAM */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="brand-bloom brand-bloom-cyan w-[500px] h-[500px] -top-32 -left-40 opacity-35" />
        <HexMark
          size={220}
          opacity={0.055}
          className="absolute -right-10 top-24 rotate-[14deg] pointer-events-none hidden lg:block"
        />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
              <div>
                <span className="chip">
                  <span className="chip-dot" /> Why M.E. Team
                </span>
                <h2 className="mt-5 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] max-w-3xl">
                  Built for high-pressure schedules and{" "}
                  <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                    high standards.
                  </span>
                </h2>
              </div>
              <p className="max-w-sm text-[var(--text-muted)]">
                A destination-management service for tours, film productions,
                association travel, and VIP guest programs.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.08}>
                <div className="grad-border card-white p-6 md:p-8 h-full relative">
                  <span className="corner-cross" style={{ top: 10, right: 10 }} />
                  <div className="icon-tile mb-6 relative z-10">
                    <w.icon size={20} />
                  </div>
                  <div className="numeral-mono text-4xl md:text-6xl absolute top-3 md:top-4 right-4 md:right-6 select-none">
                    0{i + 1}
                  </div>
                  <h3 className="font-editorial text-lg md:text-2xl leading-tight relative">
                    {w.title}
                  </h3>
                  <p className="mt-3 text-[var(--text-muted)] text-sm leading-relaxed relative">
                    {w.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="relative py-8 md:py-16">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-3 md:gap-4">
            {[
              { src: IMG.taxi, alt: "Private transport", kicker: "Private Transport", title: "Chauffeurs · Motorcades", span: "col-span-6 md:col-span-4" },
              { src: IMG.sail, alt: "Luxury travel", kicker: "Leisure", title: "Yachts · Sail Charters", span: "col-span-6 md:col-span-4", delay: 0.05 },
              { src: IMG.yacht, alt: "Executive VIP travel", kicker: "VIP Travel", title: "Private Jet · Executive Suites", span: "col-span-12 md:col-span-4", delay: 0.1 },
              { src: IMG.friends, alt: "Luxury accommodations", kicker: "Accommodations", title: "Villas · Boutique Hotels", span: "col-span-6 md:col-span-4", delay: 0.15 },
              { src: IMG.wedding, alt: "Weddings", kicker: "Weddings", title: "Destination · Signature Days", span: "col-span-6 md:col-span-4", delay: 0.2 },
              { src: "/vip-film/entertainment-support-concert.webp", alt: "Entertainment support", kicker: "Entertainment Support", title: "Artists · Concerts · Tours", span: "col-span-12 md:col-span-4", delay: 0.25 },
            ].map((t, i) => (
              <Reveal key={t.alt} className={t.span} delay={t.delay || 0}>
                <div className="group relative aspect-[4/3] overflow-hidden rounded-2xl">
                  <img src={t.src} alt={t.alt} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]" />
                  {/* Bottom gradient — becomes richer on hover */}
                  <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />
                  <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                    <div
                      className="font-mono-brand text-[10px] md:text-[11px] uppercase tracking-[0.28em] text-cyan-300"
                      data-testid={`vip-tile-kicker-${i}`}
                    >
                      {t.kicker}
                    </div>
                    <h3
                      className="mt-1.5 font-editorial text-white text-xl md:text-2xl leading-tight tracking-[-0.01em]"
                      data-testid={`vip-tile-title-${i}`}
                    >
                      {t.title}
                    </h3>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MID-PAGE BOOK-A-CALL BANNER */}
      <section className="relative py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden card-dark px-6 md:px-12 py-10 md:py-12 group">
              <div className="absolute inset-0 grid-pattern-dark opacity-40 pointer-events-none" />
              <div className="absolute -top-16 -right-20 w-[360px] h-[360px] rounded-full bg-cyan-500/25 blur-3xl" />

              <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex items-start gap-4">
                  <span className="shrink-0 w-11 h-11 rounded-full bg-cyan-500/15 border border-cyan-300/30 flex items-center justify-center text-cyan-300">
                    <Sparkles size={18} />
                  </span>
                  <div>
                    <p className="font-mono-brand text-[10px] uppercase tracking-[0.24em] text-cyan-300/85">
                      Talent, cast, or executive traveling soon?
                    </p>
                    <h3 className="mt-2 font-editorial text-2xl md:text-3xl leading-tight text-white text-balance max-w-2xl">
                      Grab 15 minutes with our VIP travel desk.
                    </h3>
                    <p className="mt-2 text-white/60 text-[14.5px] leading-relaxed max-w-xl">
                      Discreet, fast, and off-the-record. We&rsquo;ll walk through the itinerary planning and shape a plan on the call.
                    </p>
                  </div>
                </div>
                <Link
                  to="/contact#book"
                  data-testid="vip-film-mid-book-cta"
                  className="btn-primary shrink-0 w-full md:w-auto justify-center group-hover:translate-x-0.5 transition-transform"
                >
                  Book a 15-min call <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6">
          <div className="card-dark relative overflow-hidden px-6 md:px-14 py-16 md:py-24">
            <div className="absolute inset-0 grid-pattern-dark opacity-40" />
            <div className="absolute -top-20 -left-20 w-[400px] h-[400px] rounded-full bg-cyan-500/15 blur-3xl" />
            <div className="absolute -bottom-24 -right-16 w-[500px] h-[500px] rounded-full bg-sky-500/15 blur-3xl" />
            <div className="relative">
              <Reveal>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
                  <div>
                    <span className="chip !bg-white/5 !text-cyan-300 !border-cyan-300/25">
                      <span className="chip-dot" /> Capabilities
                    </span>
                    <h2 className="mt-6 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.03em] text-white max-w-3xl">
                      Every detail — so your team can focus on{" "}
                      <span className="bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent italic font-medium">
                        performance.
                      </span>
                    </h2>
                  </div>
                  <p className="max-w-sm text-white/60">
                    Ambitious visions become reality. Twelve destination-management
                    modules, orchestrated as one.
                  </p>
                </div>
              </Reveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {CAPABILITIES.map((c, i) => (
                  <Reveal key={c.name} delay={i * 0.04}>
                    <div className="group relative bg-white/[0.04] hover:bg-white/[0.07] border border-white/10 hover:border-cyan-400/40 rounded-2xl p-6 transition-all duration-500 h-full">
                      <div className="icon-tile mb-5"><c.icon size={20} /></div>
                      <span className="font-mono-brand text-[10px] uppercase tracking-[0.22em] text-cyan-300/70">
                        /{String(i + 1).padStart(2, "0")}
                      </span>
                      <h3 className="font-editorial text-lg leading-tight text-white mt-1">{c.name}</h3>
                      <p className="mt-3 text-white/60 text-[13px] leading-relaxed">{c.body}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative py-20 md:py-28 bg-[var(--surface)] overflow-hidden">
        <div className="absolute inset-0 hex-pattern opacity-50 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex items-baseline justify-between mb-12 md:mb-16">
              <span className="chip"><Ship size={12} /> In their words</span>
              <span className="hidden md:block font-editorial text-[var(--text-muted)]">
                22+ positive reviews
              </span>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="card-white p-8 h-full flex flex-col">
                  <span className="font-editorial text-5xl leading-none text-[var(--accent)]">&ldquo;</span>
                  <p className="mt-3 text-[var(--text)] leading-relaxed text-[15px] flex-1">
                    {t.quote}
                  </p>
                  <div className="mt-6 pt-6 border-t border-[var(--border)]">
                    <div className="font-editorial text-lg leading-tight">{t.name}</div>
                    <div className="mt-1 text-[var(--text-muted)] text-xs">{t.role}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENT LOGOS */}
      <ClientLogos />

      {/* CLOSING CTA */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="card-dark relative overflow-hidden px-8 md:px-16 py-20 md:py-24 text-center">
            <div className="absolute inset-0 grid-pattern-dark opacity-40" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[70%] h-[300px] cyan-radial" />
            <div className="relative">
              <span className="chip !bg-white/5 !text-cyan-300 !border-cyan-300/25">
                <span className="chip-dot" /> Ready to plan?
              </span>
              <h2 className="mt-8 font-editorial text-4xl md:text-6xl lg:text-7xl leading-[1] tracking-[-0.03em] text-white max-w-4xl mx-auto text-balance">
                Let&apos;s design the itinerary{" "}
                <span className="bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent italic font-medium">
                  that runs like clockwork.
                </span>
              </h2>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <Link to="/contact" className="btn-primary">
                  Request a Quote <ArrowRight size={14} />
                </Link>
                <Link to="/services" className="btn-dark border-white/20">
                  Explore Our Services
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
